import { Box, Typography, List, Divider, ListItem, Chip, TextField, Tooltip, IconButton, Snackbar, Alert, Dialog } from "@mui/material";
import type { IRecipe, IRecipeIngredient, ISavedRecipes, IRecipeTag } from "../types/recipe";
import { useMemo, useCallback, useState } from "react";
import type { RootState } from "../store";
import { useSelector } from "react-redux";
import Theme from "../utils/theme";
import { capitalizeFirstLetter } from "../utils/helpers/capitalizeFirstLetter";
import { categoryImageMap } from "../utils/helpers/categoryImageMap";
import underscoreReplacer from "../utils/helpers/underscoreReplacer";
import { filterProcedureTimer } from "../utils/helpers/recipeHelper";
import CustomButton from "./default/CustomButton";
import { useTimers } from "./TimerProvider";
import { parseUpperBoundDuration } from "../utils/helpers/recipeHelper";
import { conversion } from "../utils/conversions";
import { CopyAll, IosShare } from "@mui/icons-material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import Loading from "./Loading";
import { scrollbarCss } from "../utils/themeScrollbar";

interface IRecipeProps {
    recipe: IRecipe;
    savedRecipe?: ISavedRecipes;
    disableAllButtons?: boolean;
}

const origin = (typeof window !== "undefined" && window.location?.origin) || process.env.REACT_APP_FRONTEND_URL || "";
const base = origin.replace(/\/$/, "");

const Recipe: React.FC<IRecipeProps> = ({ recipe, savedRecipe, disableAllButtons = false }) => {
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const authState = useSelector((state: RootState) => state.auth);

    const { addTimer } = useTimers();
    const recipeData = recipe;
    const [servings, setServings] = useState(1);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
        open: false,
        message: "",
        severity: "success",
    });
    const [openShareModal, setOpenShareModal] = useState(false);
    const [recipeCode, setRecipeCode] = useState("");
    const recipeUrl = !!savedRecipe ? `${base}/recipe/` + savedRecipe._id : "";

    const unlinkable = savedRecipe ? savedRecipe.user?.username !== authState.account?.username : !recipe?.generationId;

    const generateRecipeCode = useCallback(() => {
        if (unlinkable) {
            return;
        }
        setLoading(true);
        axios
            .post(`${process.env.REACT_APP_API_URL}/recipes/generate/code`, {
                recipeId: savedRecipe ? savedRecipe._id : recipe.generationId,
                recipeModel: savedRecipe ? "Recipe" : "Generation",
            })
            .then(async (res) => {
                const shareUrl = `${base}/recipe/code/${res.data.body}`;

                setRecipeCode(shareUrl);
            })
            .catch(() => {
                setToast({ open: true, message: "Error creating recipe link.", severity: "error" });
            })
            .finally(() => setLoading(false));
    }, [recipe, savedRecipe, unlinkable]);

    const handleOpenShareModal = useCallback(() => {
        setOpenShareModal(true);
    }, []);

    const copyUrlToClipboard = useCallback(
        async (type: "base" | "code") => {
            try {
                await navigator.clipboard.writeText(type === "base" ? recipeUrl : recipeCode);
                setToast({ open: true, message: "URL copied to clipboard!", severity: "success" });
            } catch {
                setToast({ open: true, message: "Couldn't copy URL.", severity: "error" });
            }
        },
        [recipeCode, recipeUrl]
    );
    const renderIngredients = useMemo(
        () => (
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: 2,
                    mt: 1,
                }}
            >
                {recipeData.ingredients.map((item: IRecipeIngredient, index: number) => {
                    const toolTipCalc = !item.ingredient ? (
                        <Box>
                            <Typography sx={{ fontWeight: 500 }}>Missing Data.</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.7 }}>
                            {/* Calories */}
                            {item.ingredient.calories ? (
                                <Typography sx={{ fontWeight: 600 }}>{Math.round(item.amount * item.ingredient.calories)} kcal</Typography>
                            ) : (
                                <Typography sx={{ opacity: 0.7 }}>No calories data.</Typography>
                            )}

                            {/* Divider */}
                            <Divider sx={{ borderColor: theme.palette.primary.main, my: 0.5 }} />

                            {/* Conversions */}
                            {item.ingredient.density ? (
                                <>
                                    <Typography>{Math.round(item.amount / item.ingredient.density)} ml</Typography>
                                    <Typography>
                                        {Math.round((item.amount / item.ingredient.density) * conversion["ml"].tablespoon)} tbsp
                                    </Typography>
                                    <Typography>
                                        {Math.round((item.amount / item.ingredient.density) * conversion["ml"].teaspoon)} tsp
                                    </Typography>
                                </>
                            ) : (
                                <Typography sx={{ opacity: 0.7 }}>No conversion data.</Typography>
                            )}
                        </Box>
                    );

                    return (
                        <Tooltip
                            key={index}
                            title={toolTipCalc}
                            placement="top"
                            arrow
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: theme.palette.background.default,
                                        color: theme.palette.primary.main,
                                        border: "2px solid " + theme.palette.primary.main,
                                        fontSize: "0.9rem",
                                        padding: "10px 14px",
                                        borderRadius: "10px",
                                        boxShadow: `0 2px 10px rgba(0,0,0,0.15)`,
                                    },
                                },
                                arrow: {
                                    sx: {
                                        color: theme.palette.primary.main,
                                    },
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    border: "1px solid",
                                    borderColor: "#E5E7EB",
                                    backgroundColor: theme.palette.background.default,
                                    py: 1,
                                    pr: 1.5,
                                    pl: 1,
                                    borderRadius: "8px",
                                    minWidth: "180px",
                                    height: "45px",
                                    flexShrink: 0,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={item.ingredient ? categoryImageMap[item.ingredient.category] : categoryImageMap.alternative}
                                    sx={{ height: "32px", width: "32px", mr: 1, cursor: "pointer" }}
                                />
                                <Box sx={{ width: "100%" }}>
                                    <Typography sx={{ cursor: "pointer" }}>{capitalizeFirstLetter(item.name)}</Typography>
                                    {item.amount > 0 && (
                                        <Box>
                                            <Divider />
                                            <Typography sx={{ cursor: "pointer" }}>{item.amount * servings} grams</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        ),
        [recipeData.ingredients, servings, theme.palette.background.default, theme.palette.primary.main]
    );

    const handleTimerClick = useCallback(
        (timeString: string, timeIndex: number) => {
            const timerValues = parseUpperBoundDuration(timeString);
            addTimer({ label: recipe.name + " step " + String(timeIndex), ...timerValues });
        },
        [addTimer, recipe.name]
    );

    const handleServingsOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const servingNumber = Number(e.target.value);
        if (Number.isNaN(servingNumber)) return setServings(1);
        if (servingNumber < 0) return setServings(0);
        if (servingNumber > 99) return setServings(99);

        return setServings(servingNumber);
    }, []);

    const renderProcedure = useMemo(
        () =>
            recipeData.procedure.map((item: string, index: number) => {
                const timeFilteredItem = filterProcedureTimer(item);

                return (
                    <ListItem key={index} sx={{ alignItems: "center", px: 0 }}>
                        <Box
                            sx={{
                                minWidth: 32,
                                height: 32,
                                borderRadius: "50%",
                                backgroundColor: "primary.main",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mr: 2,
                                mt: 0.5,
                                flexShrink: 0,
                            }}
                        >
                            {index + 1}
                        </Box>
                        <Typography>
                            {timeFilteredItem.map((fragment: { text: string; isTime: boolean }, index: number) => {
                                if (fragment.isTime) {
                                    return (
                                        <CustomButton
                                            key={index}
                                            onClick={() => handleTimerClick(fragment.text, index + 1)}
                                            label={fragment.text}
                                            sx={{ mx: 1, color: "white", fontSize: ".75em" }}
                                        />
                                    );
                                } else {
                                    return fragment.text;
                                }
                            })}
                        </Typography>
                    </ListItem>
                );
            }),
        [handleTimerClick, recipeData.procedure]
    );

    const renderTags = useMemo(
        () => (
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    padding: 1,
                }}
            >
                {recipeData.tags.map((tag: IRecipeTag, index) => (
                    <Chip
                        key={index}
                        label={underscoreReplacer(tag.tag.name)}
                        clickable={false}
                        onClick={() => {}}
                        sx={{
                            cursor: "default",
                            backgroundColor: theme.palette.mode === "light" ? theme.palette.secondary.main : theme.palette.primary.main,
                            color: theme.palette.getContrastText(
                                theme.palette.mode === "light" ? theme.palette.secondary.main : theme.palette.primary.main
                            ),
                            fontWeight: 500,
                        }}
                        size="small"
                    />
                ))}
            </Box>
        ),
        [theme, recipeData]
    );

    return (
        <Box sx={{ px: 2 }}>
            <Box sx={{ minHeight: "112px", flexShrink: 0 }}>
                <Box sx={{ marginTop: "10px", width: "calc(100% - 20px)", display: "flex", alignItems: "center" }}>
                    <Box>
                        <Typography variant="h4">{recipeData.name}</Typography>
                        {recipeData.user && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography component="a" href={`/profile/${recipeData.user.username}`} sx={{ cursor: "pointer" }}>
                                    {recipeData.user.username}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {!unlinkable && (
                        <>
                            {!disableAllButtons && (
                                <IconButton disabled={loading} onClick={handleOpenShareModal}>
                                    {!loading ? <IosShare /> : <Loading size="24px" />}
                                </IconButton>
                            )}
                        </>
                    )}
                </Box>
                <Box>{renderTags}</Box>
                <Box sx={{ marginBottom: "10px" }}>
                    <Typography>{recipeData.description}</Typography>
                </Box>
            </Box>
            <Divider></Divider>
            <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ marginTop: "10px", display: "flex" }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                            <Typography variant="h5" sx={{ flexGrow: 1 }}>
                                Ingredients
                            </Typography>
                            <Box
                                sx={{
                                    width: "115px",
                                    display: "flex",
                                    border: "1px solid",
                                    borderColor: "#E5E7EB",
                                    backgroundColor: theme.palette.background.default,
                                }}
                            >
                                <Typography sx={{ display: "flex", alignItems: "center", pr: 1, pl: 1 }}>Servings</Typography>
                                <Divider orientation="vertical" />
                                <TextField
                                    sx={{ width: "40px", pl: 1, pr: 1 }}
                                    variant="standard"
                                    value={servings}
                                    onChange={(e) => handleServingsOnChange(e)}
                                />
                            </Box>
                        </Box>
                        <List>{renderIngredients}</List>
                    </Box>
                </Box>
                <Box>
                    <Typography variant="h5">Steps</Typography>
                    <List>{renderProcedure}</List>
                </Box>
            </Box>

            <Snackbar
                open={toast.open}
                autoHideDuration={3500}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    severity={toast.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>

            <Dialog open={openShareModal} onClose={() => setOpenShareModal(false)}>
                <Box sx={{ display: "flex", flexDirection: "column", p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h6">Recipe default URL</Typography>
                        <Tooltip
                            placement="top"
                            title="Default URL for the recipe, with all functionalities and visibility detection."
                            arrow
                        >
                            <IconButton size="small" sx={{ p: 0.5 }}>
                                <InfoOutlined fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                            sx={{
                                backgroundColor: "#fff",
                                borderRadius: 1,
                                "& .MuiFilledInput-root": {
                                    pt: 3,
                                    borderRadius: 1,
                                    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.12)",
                                    "&:hover": { boxShadow: "inset 0 0 0 2px #F97316" },
                                    "&.Mui-focused": { boxShadow: "inset 0 0 0 2px #F97316", backgroundColor: "#fff" },
                                },
                            }}
                        >
                            <Box sx={{ maxWidth: "200px", width: "100%", overflow: "auto", ...scrollbarCss }}>
                                <Typography>{recipeUrl}</Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={() => copyUrlToClipboard("base")}>
                            <CopyAll />
                        </IconButton>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h6">Recipe quickview URL</Typography>
                        <Tooltip
                            placement="top"
                            title="A preview of the recipe that bypasses visibility, but does not have access to all functionalities."
                            arrow
                        >
                            <IconButton size="small" sx={{ p: 0.5 }}>
                                <InfoOutlined fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {!!recipeCode ? (
                            <>
                                <Box
                                    sx={{
                                        backgroundColor: "#fff",
                                        borderRadius: 1,
                                        "& .MuiFilledInput-root": {
                                            pt: 3,
                                            borderRadius: 1,
                                            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.12)",
                                            "&:hover": { boxShadow: "inset 0 0 0 2px #F97316" },
                                            "&.Mui-focused": { boxShadow: "inset 0 0 0 2px #F97316", backgroundColor: "#fff" },
                                        },
                                    }}
                                >
                                    <Typography sx={{ maxWidth: "200px", width: "100%", overflow: "auto", ...scrollbarCss }}>
                                        {recipeCode}
                                    </Typography>
                                </Box>
                                <IconButton onClick={() => copyUrlToClipboard("code")}>
                                    <CopyAll />
                                </IconButton>
                            </>
                        ) : (
                            <CustomButton
                                label={loading ? "Generating ..." : "Generate Code"}
                                disabled={loading}
                                sx={{ color: "white", width: "100%", mt: 1 }}
                                onClick={generateRecipeCode}
                            />
                        )}
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default Recipe;
